/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Box, Stack } from "@mui/material";
import { navCategorySelectFlyoutStyle } from "./navCategorySelectFlyoutStyle";
import { NavigationItemLink } from "@/src/types/Navigation.ts";
import Typography from "@mui/material/Typography";
import { Link } from "react-router";
import { mergeSx } from "@/src/utils/mergeSx.ts";
import { CarouselProvider, useCarousel } from "@/src/components/Carousel/CarouselProvider.tsx";
import { CarouselSlides } from "@/src/components/Carousel/CarouselSlides.tsx";
import { CarouselSlide } from "@/src/components/Carousel/CarouselSlide.tsx";
import { useLayoutEffect } from "react";

export type NavCategorySelectFlyoutProps = {
  id?: string;
  items: NavigationItemLink[];
  active?: boolean;
};

export default function NavCategorySelectFlyout({ active, items, id }: NavCategorySelectFlyoutProps) {
  return (
    <Stack
      sx={mergeSx(navCategorySelectFlyoutStyle.navCategorySelectFlyout, active && navCategorySelectFlyoutStyle.active)}
      direction={"row"}
      useFlexGap
      id={id}
      aria-hidden={!active}
    >
      <Box sx={navCategorySelectFlyoutStyle.links}>
        <CarouselProvider watchDrag watchDragNonTouch dragFree inViewThreshold={0} containScroll={"trimSnaps"}>
          <NavCategorySelectFlyoutCarouselInner items={items} />
        </CarouselProvider>
      </Box>
    </Stack>
  );
}

function NavCategorySelectFlyoutCarouselInner({ items }: Pick<NavCategorySelectFlyoutProps, "items">) {
  const { carouselApi } = useCarousel();

  useLayoutEffect(() => {
    const activeItemIdx = items.findIndex((item) => item.active);

    if (activeItemIdx >= 0 && carouselApi) {
      carouselApi.scrollTo(activeItemIdx, true);
    }
  }, [carouselApi, items]);

  return (
    <CarouselSlides>
      {items.map((item) => {
        return (
          <CarouselSlide key={item.label + item.type} setInert={false}>
            <Box sx={{ padding: "0 0.5rem" }}>
              <Typography
                component={Link}
                to={item.to}
                sx={mergeSx(navCategorySelectFlyoutStyle.link, item.active && navCategorySelectFlyoutStyle.linkActive)}
                variant={"button"}
                key={item.label + item.type}
                aria-current={item.active ? "page" : undefined}
              >
                {item.label}
              </Typography>
            </Box>
          </CarouselSlide>
        );
      })}
    </CarouselSlides>
  );
}
